/**
 * AppStateManager is a component that manages the app state.
 *
 * It compiles the app state from various sources and any time a change is detected and broadcasts to various destinations, including:
 *
 * VoiceProvider.updateState() which save the AppState into the VoiceProvider context. This is partly because we invoke
 * VoiceProvider higher up in the application hierarchy so the redux state is not available.
 *
 * Redux: Which is used by ChatDrawer.
 */
'use client'

import { authSelectors } from 'ducks/auth'
import { chatDrawerActions } from 'ducks/chatDrawer'
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { lastCommandTimestampSelectors } from 'reducer/designer/lastCommandTimetamp'
import { systemsSelectors } from 'reducer/designer/systems'
import { RootState } from 'types/state'
import { resolveCurrentProjectId } from 'util/resolveCurrentProjectId'
import { useVoice } from 'voice/OpenAIRealtimeVoiceAPI/VoiceContext'

interface AppStateManagerProps {}

const DEBUG = false

export const ALLOW_ANONYMOUS_CHAT = false

const getSystemSummary = (system: any, isSelected: boolean) => {
  const summaryText = system.getSummary({ output: true, price: true, isSelected: isSelected })

  // Include structured component data for AI to access
  return {
    summary: summaryText,
    uuid: system.uuid,
    name: system.getName(),
    isSelected: isSelected,
    batteries: system.batteries().map((battery: any) => ({
      uuid: battery.uuid,
      code: battery.code,
      battery_id: battery.battery_id, // activation ID
      quantity: battery.quantity || 1,
      manufacturer_name: battery.manufacturer_name,
      kwh_optimal: battery.kwh_optimal,
    })),
    inverters: system.inverters().map((inverter: any) => ({
      uuid: inverter.uuid,
      code: inverter.code,
      inverter_id: inverter.inverter_id, // activation ID
      manufacturer_name: inverter.manufacturer_name,
    })),
    others: system.others().map((other: any) => ({
      uuid: other.uuid,
      code: other.code,
      other_id: other.other_id, // activation ID
      quantity: other.quantity || 1,
      manufacturer_name: other.manufacturer_name,
    })),
  }
}

export default function AppStateManager(props: AppStateManagerProps) {
  const dispatch = useDispatch()
  const { updateState } = useVoice()

  // Track mounting status to prevent state updates after unmount
  const isMountedRef = useRef(true)
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Track design hash to handle undefined initial values
  const [designHash, setDesignHash] = useState<number | undefined>(undefined)

  const history = useHistory()

  // App state
  const reduxProjectId = useSelector((state: RootState) => state.projectId)
  // @TODO: Use a different method for a customer. Even a customer requires this because they may have projects belonging to more than one org
  // and it's important that they don't accidentally access with the wrong org context.
  // Currently the only way we know to get the orgId for a customer when viewing a share link is from the storage.
  const orgId = (useSelector(authSelectors.getOrgId) || window.getStorage().getItem('org_id')) as number
  const [chatContextState, setChatContextState] = useState({})
  const isPro = !!useSelector(authSelectors.getCurrentRole)

  // Get the last command timestamp from Redux state
  const lastCommandTimestamp = useSelector(lastCommandTimestampSelectors.value)
  const selectedSystemUuid = useSelector(systemsSelectors.selectedSystem)

  const getChatContextState = () => {
    const offsetInMinutes = new Date().getTimezoneOffset()
    const offsetInHours = -offsetInMinutes / 60
    const timezoneOffsetString = offsetInHours > 0 ? `+${offsetInHours}` : offsetInHours

    const projectId = resolveCurrentProjectId(history.location.pathname, reduxProjectId)

    return {
      os_ada_code: isPro ? 'pro' : 'customer',
      org_id: orgId,
      pathname: history.location.pathname,
      project_id: projectId ?? null,

      // @TODO: We use this hack because the project form will only be available when in the project zone, so this avoids
      // issues when we are outside the project zone, but ideally we should have a more robust way to access the project form
      // and safely ignore it when there is no active project form available.
      dirty_fields: window.projectForm?.mutators.getFormDirtyFields(),

      timezone_offset_hours: timezoneOffsetString,

      design: {
        systems: {
          summary: window.editor
            ?.getSystems()
            .map((system) => getSystemSummary(system, system.uuid === selectedSystemUuid)),
        },
      },
    }
  }

  const getChatContextUpdateMessageSuffix = () => {
    /* Check if app state has changed. If so, inject updated state in the next message */
    // for each key, check the serialized value of the object
    let chatContextStateKeysToUpdate: string[] = []
    const newChatContextState = getChatContextState()
    let keys = new Set([...Object.keys(newChatContextState), ...Object.keys(chatContextState)])

    keys.forEach((key) => {
      if (JSON.stringify(newChatContextState[key]) !== JSON.stringify(chatContextState[key])) {
        chatContextStateKeysToUpdate.push(`APP_STATE.${key}: ${JSON.stringify(newChatContextState[key])}`)
      }
    })

    return { chatContextStateKeysToUpdate, newChatContextState }
  }

  // Update voice chat context when pathname changes
  // Update the full state dict as things change in the app, and send it to the model on each change.
  // If the dataChannel is not yet active, we just wait until it is. When dataChannel is initialized, we send the current
  // state to the model. This should keep the model up-to-date with app state at all times.
  useEffect(() => {
    const { chatContextStateKeysToUpdate, newChatContextState } = getChatContextUpdateMessageSuffix()

    if (chatContextStateKeysToUpdate.length > 0 && isMountedRef.current) {
      setChatContextState(newChatContextState)

      // Broadcast to the voice provider
      updateState(newChatContextState)

      // Broadcast to the chat drawer
      dispatch(chatDrawerActions.setAppState(newChatContextState))
    }
  }, [
    history.location.pathname,
    orgId,
    reduxProjectId,
    JSON.stringify(window.projectForm?.mutators.getFormDirtyFields()),
    lastCommandTimestamp,
    selectedSystemUuid,
  ])

  return <></>
}
